import cuid from 'cuid'

import mixpanel from '@disco/api/mixpanel'
import api, { serializeAggregatedTrack } from '@disco/api/server'
import {
  AGGREGATED_SEARCH_QUERY,
  AGGREGATED_SEARCH_QUERY_FAIL,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE_FAIL,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE_SUCCESS,
  AGGREGATED_SEARCH_QUERY_SUCCESS,
  CHANNELS_SEARCH_QUERY,
  INBOX_SEARCH_PARAMS_UPDATE,
  SEARCH_ADD_METADATA_FILTER,
  SEARCH_CLEAR,
  SEARCH_HISTORY_APPEND,
  SEARCH_PARAMS_UPDATE,
  SEARCH_QUERY,
  SEARCH_QUERY_FAIL,
  SEARCH_QUERY_SUCCESS,
  SEARCH_REMOVE_METADATA_FILTER,
  RECIPIENT_SEARCH_PARAMS_UPDATE,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import {
  AGGREGATED_SEARCH,
  SHOWCASES,
  SIM_SEARCH,
} from '@disco/constants/FeatureTypes'
import {
  CUSTOM_CHANNEL,
  PERSONAL_LIBRARY,
  INBOX_TYPES,
} from '@disco/constants/GroupTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import { SORT_DIRECTION_DESC } from '@disco/constants/SortTypes'
import { getDiscoTrackUrl } from '@disco/utils/getTrackSearchURL'
import { getYouTubeId } from '@disco/utils/getVideoEmbedURL'
import { buildGlobalV2SearchQuery } from '@disco/utils/global-search'
import { normalizeV2Track } from '@disco/utils/search-page'
import { getPaneParamsBasedOnSearchType } from '@disco/utils/panes'
import { checkSetFilters, getFiltersWithValidValues } from '@disco/utils/search'
import { endOfDay, isSameDay, startOfDay } from 'date-fns'
import captureError from '@disco/utils/captureError'

import type { GetState } from 'types/redux'
import type { V2SearchQuery } from 'types/search-page'
import {
  loadDefaultPanel,
  loadPaneWithSearchResults,
} from '@disco/state-management/actions/ExplorerActions'
import {
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import type {
  SearchFilters,
  State as SearchState,
} from '@disco/state-management/reducers/search'

import { getNativeTagsById } from '@disco/state-management/selectors/tags'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { calculateDateRangeLabel } from '@disco/utils/date'
import { formatDurationRangeToString } from '@disco/utils/time-utils'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getGroup } from '@disco/state-management/selectors/groups'

import { AGGREGATED_SEARCH_COUNT, SOURCE_SCOPE } from '@disco/constants/search'

/**
 * Updates search parameters in the Redux store.
 * @param newSearchParams - Partial search state object with new parameter values
 * @returns Action object to update search parameters
 */
export function updateSearchParams(newSearchParams: Partial<SearchState>): {
  params: Partial<SearchState>
  type: string
} {
  return {
    type: SEARCH_PARAMS_UPDATE,
    params: newSearchParams,
  }
}
/**
 * Adds a metadata filter to the current search criteria.
 * @param filter - The metadata filter to add to the search
 * @returns Action object to add the metadata filter
 */
export const addMetadataFilter = (
  filter: SearchState['filters'],
): {
  filter: Array<any>
  type: string
} => ({
  type: SEARCH_ADD_METADATA_FILTER,
  filter,
})
/**
 * Removes a metadata filter from the current search criteria by index.
 * @param filterIndex - The index of the filter to remove
 * @returns Action object to remove the metadata filter
 */
export const removeMetadataFilter = (
  filterIndex: number,
): {
  filterIndex: number
  type: string
} => ({
  type: SEARCH_REMOVE_METADATA_FILTER,
  filterIndex,
})
/**
 * Clears search results and optionally resets the search query.
 * @param keepQuery - Whether to preserve the current search query
 * @returns Thunk that clears search and loads default panel if no filters
 * remain and search results were actually loaded
 */
export const clearSearch = (
  keepQuery: boolean,
): ((dispatch: any, getState: GetState) => void) => {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // list stays null until a search query runs, so it tells us whether
    // there are search results on screen that need replacing
    const hasSearchResults = getState().search.list !== null
    dispatch({
      type: SEARCH_CLEAR,
      keepQuery,
    })
    const hasFilters = checkSetFilters(getState().search)

    if (!hasFilters && hasSearchResults) {
      dispatch(loadDefaultPanel())
    }
  }
}

const adjustFilterValue = (value: number | string, delta: -1 | 1): string => {
  if (typeof value === 'number' || value.match(/^[0-9]+$/)) {
    const valueYear = typeof value === 'number' ? value : parseInt(value)
    return (valueYear + delta).toString()
  } else {
    return value
  }
}

/**
 * Processes filter objects and returns structured values based on filter type.
 * Handles value adjustments for different filter types (range, less than, more than, etc.).
 * @param filter - The filter object to process
 * @returns Processed filter value structure
 */
export const getFilterValue = (
  filter: Record<string, any>,
):
  | {
      max: any
      min: any
    }
  | {
      max: string
    }
  | {
      min: string
    }
  | {
      value: any
    } => {
  const { value, type, start, end } = filter

  if (type === 'range') {
    return {
      min: start,
      max: end,
    }
  } else if (type === 'less than' || type === 'before') {
    return {
      max: adjustFilterValue(value, -1),
    }
  } else if (type === 'more than' || type === 'after') {
    return {
      min: adjustFilterValue(value, 1),
    }
  }

  return {
    value,
  }
}

const getSearchMixpanelProps = (
  searchState,
  metadataFiltersSet,
  extraProps,
) => {
  const {
    searchTerm,
    type,
    groups,
    tags,
    sort,
    sortDirection,
    startDate,
    endDate,
    metadataKeys,
    filters,
    trackTags,
    duration,
  } = searchState
  let searchType = 'Regular search'
  const readableSortDirection =
    !sortDirection || sortDirection === SORT_DIRECTION_DESC
      ? 'descending'
      : 'ascending'
  const searchEventProps: Record<string, any> = {
    'Search term': searchTerm,
    'Sort order': `${sort} ${readableSortDirection}`,
  }

  const metaFilters = filters
    .map(filter => {
      if (metadataKeys.some(metadata => metadata.key === filter.key)) {
        return { [filter.key]: filter.value }
      }
      return null
    })
    .filter(Boolean)

  const dateRangeLabel = calculateDateRangeLabel(startDate, endDate)
  const durationRange = formatDurationRangeToString(duration)

  if (
    type ||
    metadataFiltersSet ||
    tags.length ||
    groups.length ||
    dateRangeLabel ||
    duration
  ) {
    searchType = 'Advanced search'
    searchEventProps['Type'] = type ? type : 'All types'
    searchEventProps['Metadata filters'] = metadataFiltersSet
      ? metaFilters
      : 'All metadata'
    searchEventProps['Groups'] = groups ? groups : 'All groups'
    searchEventProps['Date range'] = dateRangeLabel
      ? dateRangeLabel
      : 'All time'
    searchEventProps['Track tags'] = trackTags.length ? trackTags : 'All tags'
    searchEventProps['Playlist tags'] = tags.length ? tags : 'All tags'
    searchEventProps['Duration'] = duration && durationRange
  }

  return {
    event: searchType,
    props: { ...searchEventProps, ...extraProps },
  }
}

let aggAbortController: AbortController | null = null
/**
 * Performs the aggregated (MultiDISCO) search as a parallel v2 search call
 * scoped to `mds`, replacing the legacy /api/aggregated_search/ and
 * websocket-based similarity endpoints.
 * @param baseV2Query - The v2 query built for the main (business) search
 * @returns Thunk that executes the aggregated search
 */
export function getAggregatedSearch(
  baseV2Query: V2SearchQuery,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (aggAbortController) aggAbortController.abort()
    const aggregatedSearchEnabled = isFeatureEnabled(getState(), {
      featureName: AGGREGATED_SEARCH,
    })

    if (aggregatedSearchEnabled) {
      dispatch({
        type: AGGREGATED_SEARCH_QUERY,
      })
      aggAbortController = new AbortController()

      const mdsQuery: V2SearchQuery = {
        ...baseV2Query,
        scope: [SOURCE_SCOPE.MDS],
        sort: ['-score'],
        size: AGGREGATED_SEARCH_COUNT,
      }
      delete mdsQuery.cursor

      api
        .searchV2(mdsQuery, aggAbortController.signal)
        .then(response => {
          const results = response.hits
            .map(normalizeV2Track)
            .map(serializeAggregatedTrack)
          dispatch(loadBatch(results))
          dispatch({
            type: AGGREGATED_SEARCH_QUERY_SUCCESS,
            params: {
              aggregatedResults: results,
              aggregatedNextPageUrl: response.pagination.cursor,
              aggregatedQueryV2: mdsQuery,
            },
          })
        })
        .catch(error => {
          dispatch({
            type: AGGREGATED_SEARCH_QUERY_FAIL,
            error,
          })
          captureError(error, { extra: { level: 'info' } })
        })
    }
  }
}
let searchAbortController: AbortController | null = null
/**
 * Executes complex search queries with various parameters and filters.
 * Main search function that handles search terms, filters, groups, tags, dates, and duration.
 * @param actionPrefix - Optional action prefix
 * @param params - Search parameters and filters
 * @returns Thunk that executes the search query
 */
export function getSearchResults(
  actionPrefix = '',
  params = {} as Partial<SearchFilters>,
): (dispatch: any, getState: GetState) => Promise<any> {
  if (searchAbortController) searchAbortController.abort()
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const showcasesEnabled = isFeatureEnabled(getState(), {
      featureName: SHOWCASES,
    })
    const hasFilters =
      checkSetFilters(getState().search) || checkSetFilters(params)
    if (!hasFilters) return
    dispatch({
      type: SEARCH_QUERY,
    })
    const {
      searchTerm: reducerSearchTerm,
      type: reducerType,
      groups: reducerGroups,
      tags,
      nativeTags,
      sort: reducerSort,
      sortDirection: reducerSortDirection,
      startDate,
      endDate,
      duration,
    } = getState().search
    // Ability to pass search term and type directly
    const searchTerm = params.searchTerm ?? reducerSearchTerm
    const type = params.type ?? reducerType
    const sortDirection = params.sortDirection ?? reducerSortDirection
    const sort = params.sort ?? reducerSort
    const groups = params.groups ?? reducerGroups

    const filters = getState().search.filters.slice()
    const state = getState()

    const selectedGroups = groups
      .map(group => getGroup(state, { groupId: group }))
      .filter(Boolean)

    const inboxGroups = selectedGroups
      .filter(group => INBOX_TYPES.includes(group.group_type))
      .filter(Boolean)
    const channelGroups = selectedGroups
      .filter(
        group =>
          CUSTOM_CHANNEL.includes(group.group_type) ||
          PERSONAL_LIBRARY.includes(group.group_type),
      )
      .filter(Boolean)

    const groupObjects = {}

    if (inboxGroups.length)
      groupObjects['Inbox'] = inboxGroups.map(group => group.name)
    if (channelGroups.length)
      groupObjects['Channel'] = channelGroups.map(group =>
        PERSONAL_LIBRARY.includes(group.group_type) ? 'Starred' : group.name,
      )

    const groupHeader = groups.length > 0 ? groups : undefined
    const query: Record<string, any> = {
      q: searchTerm,
      search_folders: true,
      include_playlist_tracks: true,
    }
    const sortParam =
      !sortDirection || sortDirection === SORT_DIRECTION_DESC
        ? `-${sort}`
        : sort

    if (type) {
      query.sort = `["${sortParam}"]`

      if (type === 'album') {
        query.type = 'playlist'
        query.playlist_type = 'album'
      } else {
        query.type = type
      }
    } else {
      query.sort = `["-is_playlist","${sortParam}"]`
      query.type = 'playlist,track'

      // Disable artist search on mobile until it's available
      if (showcasesEnabled && !__REACT_NATIVE__) {
        query.sort = `["-is_playlist","-is_track","${sortParam}"]`
        query.type = 'artist,playlist,track'
      }
    }

    let metadataFiltersSet = false

    if (nativeTags.length) {
      query.native_tags = JSON.stringify(nativeTags)
      query.type = 'track'
    }

    if (filters.length) {
      // get regular meta filters
      const metaFilters = filters.filter(filter => {
        return filter.key.indexOf('disco_') === -1
      })
      let metaFilterObject: Record<
        string,
        ReturnType<typeof getFilterValue>
      > | null = null
      const validFilters = getFiltersWithValidValues(metaFilters)
      validFilters.forEach(filter => {
        metaFilterObject = {
          ...metaFilterObject,
          [filter.key]: getFilterValue(filter),
        }
      })
      if (metaFilterObject) query.filters = JSON.stringify(metaFilterObject)
      // get custom filters
      const customFilters = filters.filter(
        filter => filter.key.indexOf('disco_') === 0 && filter.value,
      )
      customFilters.forEach(({ key, value }) => {
        // special handling for DISCO comments (Notes), as backend expects them as separate params
        const backendParam = key.replace(/^disco_/, '')
        query[backendParam] = value
      })
      metadataFiltersSet = metaFilterObject || customFilters.length

      // if user sets track metadata filters ask backend to return tracks first
      if (!type && (metaFilterObject || customFilters.length > 0)) {
        query.sort = `["is_playlist","${sortParam}"]`
      }
    }

    if (tags.length) query.tags = tags.join(',')

    if (startDate && endDate) {
      const allTime =
        (startDate.getTime() === new Date(0).getTime() ||
          startDate.getTime() < 0) &&
        isSameDay(endDate, new Date())

      if (!allTime) {
        query.start_date = startOfDay(startDate).toISOString()
        query.end_date = endOfDay(endDate).toISOString()
      }
    }

    if (duration) {
      query.duration = duration
    }

    if (
      isFeatureEnabled(getState(), {
        featureName: SIM_SEARCH,
      })
    ) {
      const youtubeId = getYouTubeId(query.q)
      const discoTrackUrl = getDiscoTrackUrl(query.q)
      const simSearchUrl = discoTrackUrl || youtubeId

      if (query.q && simSearchUrl) {
        if (youtubeId) {
          query.simSearch = {
            type: 'youtube',
            youtubeId,
            url: query.q,
          }
        }

        if (discoTrackUrl) {
          query.simSearch = {
            type: 'disco',
            discoTrackUrl,
            url: query.q,
          }
        }

        query.q = ''
        mixpanel.sendPureEvent('Similarity Search by URL attempted', {
          URL: query.simSearch.url,
        })
      }
    }

    const searchStartTime = new Date()
    const paneParams = getPaneParamsBasedOnSearchType({
      type,
      sort,
      sortDirection,
    })
    dispatch(
      loadPaneWithSearchResults(
        {
          ...paneParams,
          index: 0,
          type: SEARCH_RESULTS_PANE,
          searching: true,
          // @ts-expect-error - TODO: fix this
          searchError: false,
          name: 'Searching...',
          groupIds: groupHeader,
          next: null,
        },
        actionPrefix,
      ),
    )

    const trackTagNames = getNativeTagsById(getState(), {
      ids: nativeTags,
    }).map(tag => tag.name)

    const searchData = {
      ...getState().search,
      trackTags: trackTagNames,
      groups: groupObjects,
    }

    // Web uses the synchronous v2 endpoint; React Native stays on the legacy
    // /api/search flow until it is migrated.
    const v2Query = __REACT_NATIVE__
      ? null
      : buildGlobalV2SearchQuery({
          searchTerm: query.q,
          type: type || '',
          sort,
          sortDirection,
          groups,
          tags,
          nativeTags,
          filters,
          startDate,
          endDate,
          duration,
          simSearchUrl: query.simSearch?.url ?? null,
          showcasesEnabled,
          size: 50,
          scope: [SOURCE_SCOPE.BUSINESS],
          // Same id on the parallel business and mds requests so analytics
          // can correlate them as one search.
          searchId: cuid(),
        })

    // Aggregated (MDS) search runs as a parallel v2 call; web only.
    if (v2Query) dispatch(getAggregatedSearch(v2Query))
    searchAbortController = new AbortController()

    const request = v2Query
      ? api.searchV2(v2Query, searchAbortController.signal).then(response => ({
          results: response.hits.map(normalizeV2Track),
          next: response.pagination.cursor,
          facet: [],
          title: response.context?.title,
        }))
      : api.getSearchResultsByQuery(
          groupHeader,
          query,
          undefined,
          searchAbortController.signal,
        )

    const reqLog = request
      .then(result => {
        const timing = new Date().getTime() - searchStartTime.getTime()
        searchAbortController = null
        dispatch({
          type: SEARCH_QUERY_SUCCESS,
          list: mapResponseToReducerObjects(dispatch, getState, result).results,
          facet: result.facet,
          searchQuery: query,
          nextPage: result.next,
          meta: {
            mixpanel: getSearchMixpanelProps(searchData, metadataFiltersSet, {
              Time: timing,
            }),
          },
        })
        // TODO: create searchPaneTitle in rendering, not in dispatch()
        let searchPaneTitle = searchTerm
          ? `Search Results for ${searchTerm}`
          : 'Search Results'

        if (query.simSearch) {
          searchPaneTitle = `Similar tracks to ${
            result?.title?.length > 0 ? result.title : query.simSearch.url
          }`
          mixpanel.sendPureEvent('Successful Similarity Search by URL', {
            URL: query.simSearch.url,
          })
        }

        dispatch(
          loadPaneWithSearchResults(
            {
              ...paneParams,
              index: 0,
              groupIds: groupHeader,
              simSearch: query.simSearch,
              searching: false,
              // @ts-expect-error - TODO: fix this
              searchError: false,
              name: searchPaneTitle,
              type: SEARCH_RESULTS_PANE,
              next: result.next,
              ...(v2Query && { searchQueryV2: v2Query }),
            },
            actionPrefix,
          ),
        )
      })
      .catch(error => {
        if (query.simSearch) {
          mixpanel.sendPureEvent('Unsuccessful Similarity Search by URL', {
            URL: query.simSearch.url,
          })
        }

        const timing = new Date().getTime() - searchStartTime.getTime()
        dispatch({
          type: SEARCH_QUERY_FAIL,
          error,
          meta: {
            mixpanel: getSearchMixpanelProps(searchData, metadataFiltersSet, {
              Time: timing,
            }),
          },
        })
        dispatch(
          loadPaneWithSearchResults(
            {
              index: 0,
              searching: false,
              // @ts-expect-error - TODO: fix this
              searchError: true,
              simSearch: query.simSearch,
              // Prefer the v2 error message, then the legacy playlist-URL
              // error shape, then a generic fallback.
              name:
                error.body?.error ??
                (error.body?.url ? error.body.url[0] : 'Search failed'),
              type: SEARCH_RESULTS_PANE,
              next: null,
            },
            actionPrefix,
          ),
        )
      })
    return reqLog
  }
}
/**
 * Loads the next page of aggregated search results by re-issuing the stored
 * mds-scoped v2 query with the pagination cursor.
 * @param cursor - Pagination cursor from the previous aggregated response
 * @returns Thunk that fetches and appends next page results
 */
export function getAggregatedSearchNextPage(
  cursor: string,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { queryV2 } = getState().search.aggregatedSearch

    if (!queryV2) return

    dispatch({
      type: AGGREGATED_SEARCH_QUERY_NEXT_PAGE,
    })

    try {
      const response = await api.searchV2({ ...queryV2, cursor })
      const results = response.hits
        .map(normalizeV2Track)
        .map(serializeAggregatedTrack)

      if (results.length) {
        dispatch(loadBatch(results))
      }

      dispatch({
        type: AGGREGATED_SEARCH_QUERY_NEXT_PAGE_SUCCESS,
        params: {
          aggregatedResults: results,
          aggregatedNextPageUrl: response.pagination.cursor,
        },
      })
    } catch (error) {
      dispatch({
        type: AGGREGATED_SEARCH_QUERY_NEXT_PAGE_FAIL,
        error,
      })
      captureError(error, { extra: { level: 'info' } })
    }
  }
}
/**
 * Finds tracks similar to the specified track using the synchronous v2
 * similarity search.
 * @param trackId - The ID of the track to find similar tracks for
 * @param aggregatedTrackURL - Optional URL for aggregated search tracks
 * @returns Thunk that finds and returns similar tracks
 */
export function getSimilarTracks(
  trackId: number,
  aggregatedTrackURL?: string,
): (dispatch: any, getState: GetState) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const isAggregatedTrack = Boolean(aggregatedTrackURL)
    const url =
      aggregatedTrackURL ||
      getTrack(getState(), { id: trackId })?.public_download_url2

    if (!url) {
      throw new Error('This track can not be used to find similar tracks.')
    }

    try {
      const response = await api.searchV2({
        query: url,
        size: 50,
        type: 'simsearch',
        scope: [isAggregatedTrack ? SOURCE_SCOPE.MDS : SOURCE_SCOPE.BUSINESS],
        sort: ['-score'],
        result_types: ['track'],
        event_data: {
          context: 'Similar tracks',
          albums_only: false,
          search_id: cuid(),
        },
      })
      const results = response.hits.map(normalizeV2Track)
      return mapResponseToReducerObjects(dispatch, getState, { results })
        .results
    } catch (error) {
      if (error?.body?.error) throw new Error(error.body.error)
      throw error
    }
  }
}
/**
 * Appends the current search to the search history.
 * @returns Thunk that adds current search to history
 */
export function appendSearchHistory(): (
  dispatch: any,
  getState: GetState,
) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: SEARCH_HISTORY_APPEND,
    })
  }
}
/**
 * Sets the search term for channels search functionality.
 * @param searchTerm - The search term to use for channel searches
 * @returns Thunk that updates the channels search term
 */
export function setChannelsSearchTerm(
  searchTerm: string,
): (dispatch: any) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CHANNELS_SEARCH_QUERY,
      channelsSearchTerm: searchTerm,
    })
  }
}
/**
 * Updates search parameters specific to inbox search functionality.
 * @param params - Partial inbox search parameters to update
 * @returns Thunk that updates inbox search parameters
 */
export function updateInboxSearchParams(
  params: Partial<SearchState['inboxSearch']>,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: INBOX_SEARCH_PARAMS_UPDATE,
      params,
    })
  }
}

/**
 * Updates search parameters specific to recipient search functionality.
 * @param params - Partial recipient search parameters to update
 * @returns Thunk that updates recipient search parameters
 */
export function updateRecipientSearchParams(
  params: Partial<SearchState['recipientSearch']>,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: RECIPIENT_SEARCH_PARAMS_UPDATE,
      params,
    })
  }
}
